// PASTE #570: client side of the server cart adjustments. When a save/load
// corrected a stale line price or dropped a sold-out line, the server returns
// `adjustments`; this records them for the in-cart / checkout notices (session
// scoped, cleared on dismiss) - the sold-out half reuses the #452 notice.

"use client";

import { readSoldOutNotice, writeSoldOutNotice } from "@/lib/cart/sold-out-notice";

export type ClientPriceChange = {
  slug: string;
  name: string;
  colorKey: string | null;
  lengthCm: number | null;
  from: number;
  to: number;
};

export type ClientCartAdjustments = {
  priceChanges: ClientPriceChange[];
  removedSoldOut: { slug: string; name: string; colorKey: string | null; lengthCm: number | null }[];
};

const NOTICE_KEY = "mag-portal-price-updated";
export const PRICE_NOTICE_EVENT = "mag-portal-price-notice";

export function hasClientAdjustments(a: ClientCartAdjustments | null | undefined): a is ClientCartAdjustments {
  return Boolean(a && ((a.priceChanges?.length ?? 0) > 0 || (a.removedSoldOut?.length ?? 0) > 0));
}

export function readPriceUpdateNotice(): ClientPriceChange[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(NOTICE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? (parsed as ClientPriceChange[]) : [];
  } catch {
    return [];
  }
}

export function clearPriceUpdateNotice(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(NOTICE_KEY);
  } catch {
    // ignore
  }
  window.dispatchEvent(new CustomEvent(PRICE_NOTICE_EVENT));
}

// Merge the server's adjustments into the session notices (latest price per
// line wins; sold-out names are de-duplicated) and wake the notice components.
export function recordCartAdjustments(a: ClientCartAdjustments | null | undefined): void {
  if (typeof window === "undefined" || !hasClientAdjustments(a)) return;
  if (a.priceChanges.length > 0) {
    const key = (p: ClientPriceChange) => `${p.slug}|${p.colorKey ?? ""}|${p.lengthCm ?? ""}`;
    const merged = new Map(readPriceUpdateNotice().map((p) => [key(p), p]));
    for (const p of a.priceChanges) {
      const prev = merged.get(key(p));
      merged.set(key(p), prev ? { ...p, from: prev.from } : p);
    }
    try {
      sessionStorage.setItem(NOTICE_KEY, JSON.stringify([...merged.values()]));
    } catch {
      // sessionStorage unavailable (private mode) - skip the notice silently.
    }
    window.dispatchEvent(new CustomEvent(PRICE_NOTICE_EVENT));
  }
  if (a.removedSoldOut.length > 0) {
    const names = new Set(readSoldOutNotice());
    for (const s of a.removedSoldOut) names.add(soldOutLabel(s));
    writeSoldOutNotice([...names]);
  }
}

function soldOutLabel(s: { name: string; colorKey: string | null; lengthCm: number | null }): string {
  return [s.name, s.lengthCm ? `${s.lengthCm} cm` : "", s.colorKey ?? ""].filter(Boolean).join(" ");
}
